import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Plus, Edit2, Send, Trash2, Eye, RefreshCw } from "lucide-react";
import api from "../../services/api";

interface ReleaseNote {
  id: number;
  version: string;
  title: string;
  content: string;
  categories: string[];
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  created_by_username: string;
}

interface FormData {
  version: string;
  title: string;
  content: string;
  categories: string[];
}

const CATEGORY_OPTIONS = [
  "New Features",
  "Bug Fixes",
  "Improvements",
  "Breaking Changes",
  "Security",
];

const EMPTY_FORM: FormData = {
  version: "",
  title: "",
  content: "",
  categories: [],
};

const ReleaseNotesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingNote, setEditingNote] = useState<ReleaseNote | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [previewMode, setPreviewMode] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery(
    ["admin-release-notes"],
    () => api.releaseNotes.getAll(),
    { staleTime: 30000 },
  );

  const notes: ReleaseNote[] = data?.data || [];

  const createMutation = useMutation(
    (formPayload: FormData) => api.releaseNotes.create(formPayload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["admin-release-notes"]);
        resetForm();
      },
    },
  );

  const updateMutation = useMutation(
    ({ id, payload }: { id: number; payload: Partial<FormData> }) =>
      api.releaseNotes.update(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["admin-release-notes"]);
        resetForm();
      },
    },
  );

  const publishMutation = useMutation(
    (id: number) => api.releaseNotes.publish(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["admin-release-notes"]);
        setConfirmPublish(null);
      },
    },
  );

  const deleteMutation = useMutation(
    (id: number) => api.releaseNotes.remove(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["admin-release-notes"]);
        setConfirmDelete(null);
      },
    },
  );

  const generateMutation = useMutation(
    () => api.releaseNotes.generateFromADO(),
    {
      onSuccess: (response: any) => {
        const generated = response.data;
        setIsCreating(true);
        setEditingNote(null);
        setFormData({
          version: generated.version || "",
          title: generated.title || "",
          content: generated.content || "",
          categories: generated.categories || [],
        });
        setGenerateError(null);
      },
      onError: (error: any) => {
        setGenerateError(
          error?.response?.data?.error ||
            "Failed to generate release notes from ADO",
        );
      },
    },
  );

  const handleGenerateFromADO = () => {
    setGenerateError(null);
    generateMutation.mutate();
  };

  const resetForm = () => {
    setEditingNote(null);
    setIsCreating(false);
    setFormData(EMPTY_FORM);
    setPreviewMode(false);
    setFormError(null);
    setGenerateError(null);
  };

  const handleEdit = (note: ReleaseNote) => {
    setEditingNote(note);
    setIsCreating(false);
    setFormData({
      version: note.version,
      title: note.title,
      content: note.content,
      categories: note.categories || [],
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingNote(null);
    setFormData(EMPTY_FORM);
  };

  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!/^\d+\.\d+\.\d+$/.test(formData.version)) {
      setFormError("Version must be valid semver (e.g., 1.0.0)");
      return;
    }

    if (editingNote) {
      updateMutation.mutate({ id: editingNote.id, payload: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const toggleCategory = (cat: string) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter((c) => c !== cat)
        : [...prev.categories, cat],
    }));
  };

  if (isError) {
    return (
      <div className="admin-section">
        <h2>Release Notes Management</h2>
        <p style={{ color: "#ef4444" }}>
          Failed to load release notes. Please try refreshing.
        </p>
      </div>
    );
  }

  const isFormOpen = isCreating || editingNote;
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="admin-section">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Release Notes Management</h2>
          <p style={{ color: "#6b7280", margin: "4px 0 0" }}>
            Create, edit, and publish release notes for all users.
          </p>
        </div>
        {!isFormOpen && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleGenerateFromADO}
              disabled={generateMutation.isLoading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                background: "#059669",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: generateMutation.isLoading ? "wait" : "pointer",
                fontWeight: 600,
                opacity: generateMutation.isLoading ? 0.7 : 1,
              }}
            >
              <RefreshCw
                size={16}
                className={generateMutation.isLoading ? "spin" : ""}
                style={
                  generateMutation.isLoading
                    ? { animation: "spin 1s linear infinite" }
                    : {}
                }
              />
              {generateMutation.isLoading
                ? "Generating..."
                : "Generate from ADO"}
            </button>
            <button
              onClick={handleCreate}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                background: "#1E40AF",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <Plus size={16} /> New Release
            </button>
          </div>
        )}
      </div>

      {generateError && (
        <p style={{ color: "#DC2626", marginBottom: 16 }}>{generateError}</p>
      )}

      {/* Form */}
      {isFormOpen && (
        <form
          onSubmit={handleSubmit}
          style={{
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: 24,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label
                htmlFor="rn-version"
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 4,
                  fontSize: "0.875rem",
                }}
              >
                Version *
              </label>
              <input
                id="rn-version"
                type="text"
                value={formData.version}
                onChange={(e) =>
                  setFormData({ ...formData, version: e.target.value })
                }
                placeholder="1.0.0"
                required
                disabled={editingNote?.is_published}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 6,
                  fontFamily: "'Fira Code', monospace",
                }}
              />
            </div>
            <div>
              <label
                htmlFor="rn-title"
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 4,
                  fontSize: "0.875rem",
                }}
              >
                Title *
              </label>
              <input
                id="rn-title"
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Performance Improvements and Bug Fixes"
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 6,
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <span
              id="rn-categories-label"
              style={{
                display: "block",
                fontWeight: 600,
                marginBottom: 4,
                fontSize: "0.875rem",
              }}
            >
              Categories
            </span>
            <div
              style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
              role="group"
              aria-labelledby="rn-categories-label"
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 16,
                    border: "1px solid #D1D5DB",
                    background: formData.categories.includes(cat)
                      ? "#1E40AF"
                      : "#fff",
                    color: formData.categories.includes(cat)
                      ? "#fff"
                      : "#374151",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <label
                htmlFor="rn-content"
                style={{ fontWeight: 600, fontSize: "0.875rem" }}
              >
                Content (Markdown) *
              </label>
              <button
                type="button"
                onClick={() => setPreviewMode(!previewMode)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#1E40AF",
                  fontSize: "0.8rem",
                }}
              >
                <Eye size={14} /> {previewMode ? "Edit" : "Preview"}
              </button>
            </div>
            {previewMode ? (
              <div
                style={{
                  minHeight: 200,
                  padding: 16,
                  border: "1px solid #D1D5DB",
                  borderRadius: 6,
                  background: "#fff",
                  lineHeight: 1.7,
                }}
              >
                {formData.content.split("\n").map((line, i) => {
                  const key = `${i}-${line.slice(0, 20)}`;
                  if (line.startsWith("## "))
                    return (
                      <h3 key={key} style={{ margin: "12px 0 6px" }}>
                        {line.slice(3)}
                      </h3>
                    );
                  if (line.startsWith("- "))
                    return (
                      <p key={key} style={{ paddingLeft: 8, margin: "2px 0" }}>
                        {line}
                      </p>
                    );
                  if (line.trim() === "") return <br key={key} />;
                  return (
                    <p key={key} style={{ margin: "2px 0" }}>
                      {line}
                    </p>
                  );
                })}
              </div>
            ) : (
              <textarea
                id="rn-content"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                placeholder={
                  "## New Features\n- Feature one\n- Feature two\n\n## Bug Fixes\n- Fixed issue..."
                }
                required
                rows={10}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 6,
                  fontFamily: "'Fira Code', monospace",
                  fontSize: "0.85rem",
                  resize: "vertical",
                }}
              />
            )}
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={resetForm}
              style={{
                padding: "8px 20px",
                border: "1px solid #D1D5DB",
                background: "#fff",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              style={{
                padding: "8px 20px",
                border: "none",
                background: "#F59E0B",
                color: "#fff",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {isSaving ? "Saving..." : editingNote ? "Update" : "Create Draft"}
            </button>
          </div>

          {(formError || createMutation.isError || updateMutation.isError) && (
            <p style={{ color: "#DC2626", marginTop: 8 }}>
              {formError ||
                (createMutation.error as any)?.response?.data?.error ||
                (updateMutation.error as any)?.response?.data?.error ||
                "An error occurred"}
            </p>
          )}
        </form>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#6B7280" }}>
          Loading release notes...
        </div>
      ) : notes.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#6B7280" }}>
          No release notes yet. Click &quot;New Release&quot; to create one.
        </div>
      ) : (
        <table className="feedback-admin-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Title</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((note) => (
              <tr key={note.id}>
                <td>
                  <span
                    style={{
                      fontFamily: "'Fira Code', monospace",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    v{note.version}
                  </span>
                </td>
                <td>{note.title}</td>
                <td>
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: 12,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      background: note.is_published ? "#DEF7EC" : "#FEF3C7",
                      color: note.is_published ? "#03543F" : "#92400E",
                    }}
                  >
                    {note.is_published ? "Published" : "Draft"}
                  </span>
                </td>
                <td style={{ fontSize: "0.85rem", color: "#6B7280" }}>
                  {new Date(
                    note.published_at || note.created_at,
                  ).toLocaleDateString()}
                </td>
                <td>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => handleEdit(note)}
                      title="Edit"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#1E40AF",
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    {!note.is_published && (
                      <button
                        onClick={() => setConfirmPublish(note.id)}
                        title="Publish"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#059669",
                        }}
                      >
                        <Send size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmDelete(note.id)}
                      title="Delete"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#DC2626",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Publish confirmation */}
      {confirmPublish !== null && (
        <div
          className="whats-new-overlay"
          aria-hidden="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmPublish(null);
          }}
        >
          <div
            role="dialog"
            aria-label="Publish confirmation"
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              maxWidth: 400,
              width: "90%",
            }}
          >
            <h3 style={{ margin: "0 0 12px" }}>Publish Release Note?</h3>
            <p style={{ color: "#6B7280", margin: "0 0 20px" }}>
              This will notify all users about the new release. This action
              cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setConfirmPublish(null)}
                style={{
                  padding: "8px 20px",
                  border: "1px solid #D1D5DB",
                  background: "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => publishMutation.mutate(confirmPublish)}
                disabled={publishMutation.isLoading}
                style={{
                  padding: "8px 20px",
                  border: "none",
                  background: "#059669",
                  color: "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {publishMutation.isLoading ? "Publishing..." : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete !== null && (
        <div
          className="whats-new-overlay"
          aria-hidden="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDelete(null);
          }}
        >
          <div
            role="dialog"
            aria-label="Delete confirmation"
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              maxWidth: 400,
              width: "90%",
            }}
          >
            <h3 style={{ margin: "0 0 12px" }}>Delete Release Note?</h3>
            <p style={{ color: "#6B7280", margin: "0 0 20px" }}>
              This will remove the release note. It can be recovered from the
              database if needed.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  padding: "8px 20px",
                  border: "1px solid #D1D5DB",
                  background: "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(confirmDelete)}
                disabled={deleteMutation.isLoading}
                style={{
                  padding: "8px 20px",
                  border: "none",
                  background: "#DC2626",
                  color: "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {deleteMutation.isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleaseNotesTab;
